import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { newsApi, resolveImageUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCrownVicImage } from '../lib/images';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, ExternalLink, Tag, ThumbsUp, ThumbsDown } from 'lucide-react';

export default function NewsDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myReaction, setMyReaction] = useState(null);
  const [reacting, setReacting] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await newsApi.getOne(id);
        setArticle(response.data);
        // Fetch user's reaction
        if (user) {
          try {
            const reactionRes = await newsApi.getMyReaction(id);
            setMyReaction(reactionRes.data.reaction);
          } catch (e) {
            // Reaction lookup is optional; user may not have reacted yet — leave myReaction null.
            console.debug('No prior reaction for article', id, e?.message);
          }
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, user]);

  const handleLike = async () => {
    if (!user || reacting) return;
    setReacting(true);
    try {
      const res = await newsApi.like(id);
      setMyReaction(res.data.reaction);
      // Re-fetch article to get updated counts
      const updated = await newsApi.getOne(id);
      setArticle(updated.data);
    } catch (e) {
      console.error(e);
    } finally {
      setReacting(false);
    }
  };

  const handleDislike = async () => {
    if (!user || reacting) return;
    setReacting(true);
    try {
      const res = await newsApi.dislike(id);
      setMyReaction(res.data.reaction);
      const updated = await newsApi.getOne(id);
      setArticle(updated.data);
    } catch (e) {
      console.error(e);
    } finally {
      setReacting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">Article not found</p>
          <Link to="/news">
            <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">Back to News</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Image */}
      <div 
        className="relative h-[50vh] min-h-[400px]"
        style={{
          backgroundImage: `url(${resolveImageUrl(article.image_url) || getCrownVicImage(article.id)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/50 to-transparent"></div>
        
        <div className="absolute top-4 left-4">
          <Link to="/news">
            <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="back-to-news-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to News
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 -mt-32 relative z-10">
        <article className="max-w-3xl mx-auto">
          {/* Meta */}
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags?.map((tag, i) => (
              <span key={i} className="tag bg-[#FF3B30] text-white px-2 py-1 rounded-sm flex items-center">
                <Tag className="w-2.5 h-2.5 mr-1" />
                {tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-white mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Author & Date & Reactions */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-[#262626]">
            <div className="w-10 h-10 rounded-full bg-[#262626] flex items-center justify-center text-white font-bold">
              {article.author_name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-white font-medium">{article.author_name || 'Crown Vic Nation'}</p>
              <p className="text-gray-500 text-sm flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {formatDate(article.created_at)}
              </p>
            </div>

            {/* Like/Dislike Buttons */}
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={handleLike}
                disabled={!user || reacting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  myReaction === 'like'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-[#1F1F1F] border-[#262626] text-gray-400 hover:border-green-500/50 hover:text-green-400'
                } ${!user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="like-btn"
                title={user ? 'Like this article' : 'Login to like'}
              >
                <ThumbsUp className={`w-5 h-5 ${myReaction === 'like' ? 'fill-green-400' : ''}`} />
                <span className="font-bold text-sm">{article.likes || 0}</span>
              </button>
              
              <button
                onClick={handleDislike}
                disabled={!user || reacting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  myReaction === 'dislike'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-[#1F1F1F] border-[#262626] text-gray-400 hover:border-red-500/50 hover:text-red-400'
                } ${!user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="dislike-btn"
                title={user ? 'Dislike this article' : 'Login to dislike'}
              >
                <ThumbsDown className={`w-5 h-5 ${myReaction === 'dislike' ? 'fill-red-400' : ''}`} />
                <span className="font-bold text-sm">{article.dislikes || 0}</span>
              </button>
            </div>

            {article.source_url && (
              <a 
                href={article.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm" className="border-[#262626] text-gray-400 hover:text-white" data-testid="source-link-btn">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Source
                </Button>
              </a>
            )}
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">
              {article.content}
            </p>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-[#262626]">
            <Link to="/news">
              <Button variant="outline" className="border-[#262626] text-white hover:bg-[#262626]" data-testid="more-news-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                More News
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
